// The dates a report covers (BL-123): the presets every report offers (today, this week, this month,
// last month, this quarter, year to date) and a custom range, each with the dates it's compared with a
// year before. All in the company's own days: the caller passes the company's today (the server's
// today(ctx), the office app's today in the company's zone), so a range never shifts with the
// browser's or the server's clock. Today and this week compare with the same weekdays (364 days
// back), so a Tuesday is set beside a Tuesday; a month, a quarter, a year to date or a custom range
// with the same dates a year before (29 February takes the 28th).
import { addDays, daysBetween, isLocalDate, type LocalDate } from '@flowology/kernel';

export const DATE_PRESETS = ['today', 'week', 'month', 'last', 'quarter', 'ytd', 'custom'] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

/** Each preset's words, in the order the page offers them. */
export const DATE_PRESET_WORDS: Readonly<Record<DatePreset, string>> = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
  last: 'Last month',
  quarter: 'This quarter',
  ytd: 'Year to date',
  custom: 'Custom',
};

/** The longest custom range, in days: a report covers a year at most. */
export const MAX_RANGE_DAYS = 366;

export interface DateRange {
  preset: DatePreset;
  from: LocalDate;
  to: LocalDate;
  /** The dates it's compared with: the same weekdays (weekly) or the same dates a year before. */
  compareFrom: LocalDate;
  compareTo: LocalDate;
  /** Compared by weekday (today, this week), 364 days back. */
  weekly: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number) => `${String(y).padStart(4, '0')}-${pad(m)}-${pad(d)}`;
const partsOf = (d: LocalDate) => d.split('-').map(Number) as [number, number, number];

/** The day of the week, 0 for Sunday. */
export const weekdayOf = (d: LocalDate): number => {
  const [y, m, dd] = partsOf(d);
  return new Date(Date.UTC(y, m - 1, dd)).getUTCDay();
};

/** The last day of a date's month. */
export function monthEnd(d: LocalDate): LocalDate {
  const [y, m] = partsOf(d);
  return addDays(m === 12 ? ymd(y + 1, 1, 1) : ymd(y, m + 1, 1), -1);
}

/** The same date a year before; 29 February takes the 28th. */
export function yearBefore(d: LocalDate): LocalDate {
  const [y, m, dd] = partsOf(d);
  const last = Number(monthEnd(ymd(y - 1, m, 1)).slice(8));
  return ymd(y - 1, m, Math.min(dd, last));
}

/** Refuses a custom range that can't be read: a reason in words, or null. */
export function customRangeRefusal(from: string | undefined, to: string | undefined): string | null {
  if (!from || !to || !isLocalDate(from) || !isLocalDate(to)) return 'Pick both dates.';
  if (to < from) return 'The range ends before it starts.';
  if (daysBetween(from, to) + 1 > MAX_RANGE_DAYS) return 'A report covers a year at most.';
  return null;
}

/**
 * The dates a preset covers on the company's `today`, and what they're compared with. "This month",
 * "this quarter" and "year to date" run to today, so they're set beside the same days a year before
 * (1–27 October beside 1–27 October). A custom range needs `from` and `to` (customRangeRefusal).
 * Weeks start on Monday unless `weekStartsOn` (0 for Sunday) says otherwise.
 */
export function dateRange(
  today: LocalDate,
  preset: DatePreset,
  custom: { from?: string | undefined; to?: string | undefined } = {},
  weekStartsOn = 1,
): DateRange {
  const [y, m] = partsOf(today);
  let from: LocalDate;
  let to: LocalDate = today;
  let weekly = false;
  switch (preset) {
    case 'today':
      from = today;
      weekly = true;
      break;
    case 'week':
      from = addDays(today, -((weekdayOf(today) - weekStartsOn + 7) % 7));
      weekly = true;
      break;
    case 'month':
      from = ymd(y, m, 1);
      break;
    case 'last':
      to = addDays(ymd(y, m, 1), -1);
      from = `${to.slice(0, 8)}01`;
      break;
    case 'quarter':
      from = ymd(y, Math.floor((m - 1) / 3) * 3 + 1, 1);
      break;
    case 'ytd':
      from = ymd(y, 1, 1);
      break;
    case 'custom': {
      const refused = customRangeRefusal(custom.from, custom.to);
      if (refused) throw new RangeError(refused);
      from = custom.from!;
      to = custom.to!;
      break;
    }
  }
  return {
    preset,
    from,
    to,
    compareFrom: weekly ? addDays(from, -364) : yearBefore(from),
    compareTo: weekly ? addDays(to, -364) : yearBefore(to),
    weekly,
  };
}

/** `n` months ending with `last` ("2026-10"), oldest first. */
export function monthsEnding(last: string, n: number): string[] {
  const y = Number(last.slice(0, 4));
  const m = Number(last.slice(5, 7)) - 1;
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(Date.UTC(y, m - (n - 1 - i), 15));
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
  });
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * A range in words, as the page says it: "Tuesday 27 October 2026", "1–27 October 2026",
 * "1 Jan–27 Oct 2026", "1 Nov 2025–27 Oct 2026".
 */
export function rangeText(from: LocalDate, to: LocalDate): string {
  const [y, m, d] = partsOf(from);
  const [y2, m2, d2] = partsOf(to);
  const mon = (k: number) => MONTHS[k - 1]!;
  if (from === to) return `${WEEKDAYS[weekdayOf(from)]} ${d} ${mon(m)} ${y}`;
  if (y === y2 && m === m2) return `${d}–${d2} ${mon(m)} ${y}`;
  if (y === y2) return `${d} ${mon(m).slice(0, 3)}–${d2} ${mon(m2).slice(0, 3)} ${y}`;
  return `${d} ${mon(m).slice(0, 3)} ${y}–${d2} ${mon(m2).slice(0, 3)} ${y2}`;
}

/** "Oct", for a month ("2026-10"). */
export const monthShort = (month: string): string => MONTHS[Number(month.slice(5, 7)) - 1]!.slice(0, 3);
